import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import Keyboard, { type KeyTone } from '../../shared/ui/Keyboard';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull } from '../../shared/dictionary';
import {
  evaluateGuess,
  mergeKeyState,
  normalizePuzzle,
  type CodeWordLevel,
  type Guess,
  type KeyState,
  type NormalizedPuzzle,
  type TileState,
} from './types';
import type { GauntletAware } from '../../shared/gauntletStep';
import './codeWord.css';

export default function CodeWordGame() {
  const [data, setData] = useState<{
    puzzle: NormalizedPuzzle;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<CodeWordLevel>('codeWord')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Code Word yet.');
          return;
        }
        const puzzle = normalizePuzzle(res.data);
        if (!puzzle) {
          setLoadError('Today\'s Code Word puzzle is malformed.');
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="codeWord" />;
  if (!data) {
    return (
      <GameShell gameId="codeWord">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('codeWord', data.dateKey)) {
    return (
      <FinishedDaily gameId="codeWord" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <CodeWordPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

export function CodeWordPlay({
  puzzle,
  dateKey,
  isToday,
  gauntlet,
}: GauntletAware & {
  puzzle: NormalizedPuzzle;
  dateKey: string;
  isToday: boolean;
}) {
  // Everything needed to rebuild the board after backing out mid-round.
  const { restored, save } = useDailySnapshot<{
    guesses: Guess[];
    keyboardState: Record<string, KeyState>;
    hinted: number[];
    hintUsed: boolean;
  }>('codeWord', dateKey);

  const [guesses, setGuesses] = useState<Guess[]>(() => restored?.guesses ?? []);
  const [currentInput, setCurrentInput] = useState('');
  const [keyboardState, setKeyboardState] = useState<Record<string, KeyState>>(
    () => restored?.keyboardState ?? {},
  );
  const [outcome, setOutcome] = useState<'won' | 'lost' | null>(null);
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [hintedPositions, setHintedPositions] = useState<Set<number>>(
    () => new Set(restored?.hinted ?? []),
  );
  const [hintUsed, setHintUsed] = useState(restored?.hintUsed ?? false);

  usePersistedState(
    save,
    { guesses, keyboardState, hinted: [...hintedPositions], hintUsed },
    !gauntlet && outcome === null,
  );

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1400);
  }, []);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  // Guarded by a ref: the host may hand a fresh `gauntlet` object each render.
  const solvedRef = useRef(false);
  useEffect(() => {
    if (outcome !== 'won' || solvedRef.current) return;
    solvedRef.current = true;
    if (gauntlet) gauntlet.onSolved();
    else
      recordOutcome('codeWord', dateKey, 'won', {
        subtitle: `Cracked ${puzzle.targetWord} in ${guesses.length} ${
          guesses.length === 1 ? 'guess' : 'guesses'
        }.`,
      });
  }, [outcome, dateKey, gauntlet, puzzle.targetWord, guesses.length]);

  // Gauntlet: burning every guess costs a life. The host ends the run, not us.
  const lostRef = useRef(false);
  useEffect(() => {
    if (outcome !== 'lost' || lostRef.current) return;
    lostRef.current = true;
    if (gauntlet) gauntlet.onWrong();
    else
      recordOutcome('codeWord', dateKey, 'lost', {
        subtitle: 'Out of guesses.',
        answer: puzzle.targetWord,
      });
  }, [gauntlet, outcome, dateKey, puzzle.targetWord]);

  const nonHintLength = puzzle.wordLength - hintedPositions.size;

  const effectiveGuess = useCallback(
    (typed: string): string => {
      const targetChars = puzzle.targetWord.split('');
      const typedChars = typed.split('');
      const out: string[] = [];
      let ti = 0;
      for (let i = 0; i < puzzle.wordLength; i++) {
        if (hintedPositions.has(i)) {
          out.push(targetChars[i]);
        } else if (ti < typedChars.length) {
          out.push(typedChars[ti++]);
        } else {
          return '';
        }
      }
      return out.join('');
    },
    [puzzle, hintedPositions],
  );

  const isAcceptedInput = useCallback(
    async (word: string): Promise<boolean> => {
      if (word === puzzle.targetWord) return true;
      if (puzzle.validWords.size > 0 && puzzle.validWords.has(word)) return true;
      return await isValidWordFull(word);
    },
    [puzzle],
  );

  const submit = useCallback(async () => {
    if (outcome !== null) return;
    if (currentInput.length !== nonHintLength) {
      triggerShake();
      showToast(`${nonHintLength} letters required`);
      return;
    }
    const fullGuess = effectiveGuess(currentInput);
    if (fullGuess.length !== puzzle.wordLength) return;

    const accepted = await isAcceptedInput(fullGuess);
    if (!accepted) {
      triggerShake();
      showToast('Not in word list');
      return;
    }

    const feedback = evaluateGuess(fullGuess, puzzle.targetWord);
    const guess: Guess = { word: fullGuess, feedback };
    const nextGuesses = [...guesses, guess];
    setGuesses(nextGuesses);
    setKeyboardState((prev) => {
      const next = { ...prev };
      for (const f of feedback) {
        const incoming: KeyState =
          f.state === 'correct'
            ? 'correct'
            : f.state === 'present'
            ? 'present'
            : f.state === 'absent'
            ? 'absent'
            : 'untouched';
        next[f.letter] = mergeKeyState(prev[f.letter] ?? 'untouched', incoming);
      }
      return next;
    });
    setCurrentInput('');

    if (fullGuess === puzzle.targetWord) {
      setOutcome('won');
    } else if (nextGuesses.length >= puzzle.maxGuesses) {
      setOutcome('lost');
    }
  }, [
    outcome,
    currentInput,
    nonHintLength,
    effectiveGuess,
    puzzle,
    isAcceptedInput,
    guesses,
    triggerShake,
    showToast,
  ]);

  const onLetter = useCallback(
    (letter: string) => {
      if (outcome !== null) return;
      if (currentInput.length >= nonHintLength) return;
      setCurrentInput((prev) => prev + letter);
    },
    [outcome, currentInput.length, nonHintLength],
  );

  const onDelete = useCallback(() => {
    if (outcome !== null) return;
    setCurrentInput((prev) => prev.slice(0, -1));
  }, [outcome]);

  const useHint = useCallback(() => {
    // The host gates hint availability in the gauntlet, so the local
    // one-per-puzzle limit doesn't apply there.
    if (outcome !== null || (hintUsed && !gauntlet)) return;
    const candidates: number[] = [];
    const lastFeedback = guesses[guesses.length - 1]?.feedback;
    for (let i = 0; i < puzzle.wordLength; i++) {
      if (hintedPositions.has(i)) continue;
      if (lastFeedback?.[i]?.state === 'correct') continue;
      candidates.push(i);
    }
    if (candidates.length === 0) {
      showToast('No more letters to reveal');
      return;
    }
    const pick = candidates[Math.floor(Math.random() * candidates.length)];
    const letter = puzzle.targetWord[pick];
    setHintedPositions((prev) => new Set(prev).add(pick));
    setHintUsed(true);
    setKeyboardState((prev) => ({
      ...prev,
      [letter]: mergeKeyState(prev[letter] ?? 'untouched', 'correct'),
    }));
    setCurrentInput((prev) => prev.slice(0, puzzle.wordLength - hintedPositions.size - 1));
    showToast(`Letter ${letter} revealed at position ${pick + 1}`);
  }, [outcome, hintUsed, guesses, puzzle, hintedPositions, showToast, gauntlet]);

  // Each increment of the host's hint signal spends one pooled hint here.
  const lastHintSignal = useRef(gauntlet?.hintSignal ?? 0);
  useEffect(() => {
    const signal = gauntlet?.hintSignal ?? 0;
    if (signal > lastHintSignal.current) {
      lastHintSignal.current = signal;
      useHint();
    }
  }, [gauntlet?.hintSignal, useHint]);

  const tileState = useCallback(
    (row: number, col: number): { letter: string; state: TileState } => {
      if (row < guesses.length) {
        const fb = guesses[row].feedback[col];
        return { letter: fb.letter, state: fb.state };
      }
      if (row === guesses.length) {
        if (hintedPositions.has(col)) {
          return { letter: puzzle.targetWord[col], state: 'correct' };
        }
        // Map column to typed-input index (skip hint positions)
        let typedIdx = 0;
        for (let i = 0; i < col; i++) {
          if (!hintedPositions.has(i)) typedIdx++;
        }
        if (typedIdx < currentInput.length) {
          return { letter: currentInput[typedIdx], state: 'typed' };
        }
        return { letter: '', state: 'empty' };
      }
      return { letter: '', state: 'empty' };
    },
    [guesses, currentInput, hintedPositions, puzzle.targetWord],
  );

  const tones: Record<string, KeyTone | undefined> = useMemo(() => {
    const out: Record<string, KeyTone | undefined> = {};
    for (const [letter, state] of Object.entries(keyboardState)) {
      if (state === 'untouched') continue;
      out[letter] = state as KeyTone;
    }
    return out;
  }, [keyboardState]);

  const revealedHints = useMemo(() => {
    return [...hintedPositions]
      .sort((a, b) => a - b)
      .map((i) => `Position ${i + 1} is ${puzzle.targetWord[i]}`);
  }, [hintedPositions, puzzle.targetWord]);

  const submitEnabled = currentInput.length === nonHintLength && outcome === null;

  const body = (
    <div className="cw-board">
        {/* Tile size is derived from the word length so a 7-letter puzzle still
            fits a 320px screen — see --cw-tile in codeWord.css. */}
        <div
          className={'cw-grid' + (shake ? ' is-shake' : '')}
          style={{ ['--cw-len' as string]: String(puzzle.wordLength) }}
        >
          {Array.from({ length: puzzle.maxGuesses }).map((_, row) => (
            <div key={row} className="cw-row">
              {Array.from({ length: puzzle.wordLength }).map((_, col) => {
                const { letter, state } = tileState(row, col);
                return (
                  <div key={col} className={`cw-tile is-${state}`}>
                    {letter}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {revealedHints.length > 0 && (
          <div className="cw-hints">
            {revealedHints.map((line) => (
              <div key={line} className="cw-hint-line">
                💡 {line}
              </div>
            ))}
          </div>
        )}

        {toast && <div className="cw-toast">{toast}</div>}

        <Keyboard
          letterTones={tones}
          onLetter={onLetter}
          onDelete={onDelete}
          onSubmit={submit}
          submitEnabled={submitEnabled}
          onHint={useHint}
          hintEnabled={outcome === null && !hintUsed && hintedPositions.size < puzzle.wordLength}
        />

        {outcome === 'won' && !gauntlet && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={`Cracked ${puzzle.targetWord} in ${guesses.length} ${
              guesses.length === 1 ? 'guess' : 'guesses'
            }.`}
          />
        )}

        {outcome === 'lost' && (
          <div className="cw-overlay">
            <div className="cw-overlay-card">
              <div className="cw-overlay-emoji">😬</div>
              <div className="cw-overlay-title">Out of guesses</div>
              <div className="cw-overlay-sub">The code was</div>
              <div className="cw-overlay-answer">{puzzle.targetWord}</div>
            </div>
          </div>
        )}
    </div>
  );

  // In the gauntlet the host supplies the chrome, so render bare.
  if (gauntlet) return <div className="gauntlet-embed">{body}</div>;

  return (
    <GameShell
      gameId="codeWord"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${guesses.length}/${puzzle.maxGuesses}`}
    >
      {body}
    </GameShell>
  );
}
